import React, { useEffect, useMemo, useState } from 'react';
import {
  Anchor,
  Alert,
  Container,
  Title,
  Text,
  Button,
  Card,
  Badge,
  List,
  ThemeIcon,
  Divider,
  Group,
  Stack,
  SimpleGrid,
  Modal,
  Paper,
} from '@mantine/core';
import { Check, ArrowRight, Download, X } from 'lucide-react';

const tiers = [
  {
    name: 'Community',
    price: 'Free',
    period: 'forever',
    description: 'Full native compilation for open-source and commercial projects.',
    badge: null,
    features: [
      'Full compilation to native machine code',
      'MIT Licensed — use it anywhere',
      'Community support via GitHub',
      'All Python 3.10–3.14 supported',
      'Windows, Linux, macOS targets',
    ],
    cta: 'Get Started',
    highlight: false,
    href: '#',
  },
  {
    name: 'Pro',
    price: null,  // filled from props
    period: 'seat / year',
    description: 'Perpetual license with 12 months of updates. Protected JWT license verification for commercial distribution.',
    badge: 'Popular',
    features: [
      'Everything in Community',
      'Protected JWT license verification',
      'Elliptic curve signature (P-256)',
      'String compression for IP hardening',
      'Perpetual license — use your builds forever',
      '12 months of version updates included',
    ],
    cta: 'View pricing & buy',
    highlight: true,
    plan: 'py2nativePro',
  },
  {
    name: 'Enterprise',
    price: null,  // filled from props
    period: 'seat / year',
    description: 'Perpetual license with 12 months of updates, priority support, and custom licensing.',
    badge: null,
    features: [
      'Everything in Pro',
      'Priority support SLA',
      'Custom licensing options',
      'Dedicated onboarding',
      'Perpetual license — use your builds forever',
      '12 months of version updates included',
    ],
    cta: 'View pricing & buy',
    highlight: false,
    plan: 'py2nativeEnterprise',
  },
  {
    name: 'Custom',
    price: null,
    period: null,
    description: 'Unlimited seats, consulting, and custom integrations for large organizations.',
    badge: null,
    features: [
      'Everything in Enterprise',
      'Unlimited seats',
      'Consulting & custom integrations',
      'Dedicated support engineer',
      'Custom SLA',
    ],
    cta: 'Schedule a Call',
    highlight: false,
  },
];

export default function Pricing({ proPrice, enterprisePrice, authServer }) {
  const [showCalendar, setShowCalendar] = useState(false);

  const authUrl = (authServer && authServer.url) || '';
  const authAppName = (authServer && authServer.appName) || 'py2native';
  const handoff = Boolean(authUrl && authAppName);

  // ---- Live storefront mode: the auth server's pricing page is embedded
  // here (single source of truth: live Stripe prices, period tabs, CTAs and
  // sign-in all come from the storefront). Buying runs in a top-level window
  // and returns to this page (?status=success|canceled) after payment.

  /** Where the buyer lands after checkout — this pricing page. */
  const storeReturnUrl = useMemo(
    () => `${window.location.origin}/pricing`,
    [],
  );

  const [storeStatus] = useState(
    () => new URLSearchParams(window.location.search).get('status') || null,
  );

  const [storeHeight, setStoreHeight] = useState(860);

  const storeSrc = useMemo(() => {
    if (!handoff) return '';
    const url = new URL(`${authUrl}/pricing/${encodeURIComponent(authAppName)}`);
    url.searchParams.set('embed', '1');
    url.searchParams.set('return', storeReturnUrl);
    return url.toString();
  }, [handoff, authUrl, authAppName, storeReturnUrl]);

  useEffect(() => {
    if (!handoff) return undefined;
    let expectedOrigin = '';
    try {
      expectedOrigin = new URL(authUrl).origin;
    } catch {
      // malformed auth URL — ignore all messages
    }
    const onMessage = (event) => {
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      const data = event.data;
      if (
        data &&
        data.type === 'auth-pricing-embed-height' &&
        typeof data.height === 'number'
      ) {
        // clamp to something sane; +12px breathing room below the table
        const next = Math.max(600, Math.min(Number(data.height) + 12, 4000));
        setStoreHeight(next);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handoff, authUrl]);

  if (handoff) {
    const licenseHref = `${authUrl}/api/license?app=${encodeURIComponent(authAppName)}&download=1`;
    const portalHref = `${authUrl}/api/stripe/portal`;
    return (
      <Container size="lg" py={40} pb={100}>
        <Stack gap="md" mb={28}>
          <Title order={2} fw={900} fz="clamp(24px, 4vw, 32px)" style={{ letterSpacing: '-0.02em' }}>
            Pricing
          </Title>

          {storeStatus === 'success' && (
            <Alert color="teal" title="Payment complete" icon={<Check size={16} />}>
              Thanks! Your license has been sent to your email — you can
              download it again any time below.
            </Alert>
          )}
          {storeStatus === 'canceled' && (
            <Alert color="yellow" title="Checkout canceled" icon={<X size={16} />}>
              You canceled the payment. No charge was made — you can try again below.
            </Alert>
          )}
          {!storeStatus && (
            <Text size="sm" c="dimmed">
              Plans, features and checkout are managed by our storefront — sign
              in to subscribe, your license arrives by email.
            </Text>
          )}
        </Stack>

        <Paper
          withBorder
          radius="lg"
          style={{ overflow: 'hidden', background: 'var(--mantine-color-body)' }}
        >
          <iframe
            src={storeSrc}
            title="Py2Native pricing & checkout"
            style={{ display: 'block', width: '100%', height: storeHeight, border: 0 }}
          />
        </Paper>

        <Group justify="center" mt={36} gap="lg">
          <Group gap="xs">
            <Download size={16} />
            <Anchor href={licenseHref} size="sm">
              Download your license
            </Anchor>
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Manage your subscription{' '}
              <Anchor href={portalHref} size="sm">
                Billing portal
              </Anchor>
            </Text>
          </Group>
        </Group>
      </Container>
    );
  }

  // ---- Legacy mode (no auth server configured): static cards + in-app
  // checkout.

  const displayTiers = tiers.map(tier => {
    if (tier.name === 'Pro' && proPrice) return { ...tier, price: `$${proPrice}` };
    if (tier.name === 'Enterprise' && enterprisePrice) return { ...tier, price: `$${enterprisePrice}` };
    return tier;
  });

  const openCheckout = (plan) => {
    const currentUrl = window.location.href;
    const params = new URLSearchParams({
      plan,
      continue: currentUrl,
      cancel: currentUrl,
      locale: 'en',
    });
    window.location.assign(`${document.location.origin}/api/buy?${params}`);
  };

  return (
    <Container size="md" py={40} pb={100}>
      <Title order={2} fw={900} mb={56} fz="clamp(24px, 4vw, 32px)" style={{ letterSpacing: '-0.02em' }}>
        Pricing
      </Title>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        {displayTiers.map((tier) => (
          <Card
            key={tier.name}
            padding="lg"
            radius="lg"
            withBorder
            style={{
              borderColor: tier.highlight ? 'var(--mantine-color-blue-filled)' : undefined,
              borderWidth: tier.highlight ? 2 : 1,
              backgroundColor: tier.highlight ? 'var(--mantine-color-blue-light)' : undefined,
              position: 'relative',
            }}
          >
            {tier.badge && (
              <Badge
                variant="filled"
                color="blue"
                size="lg"
                style={{ position: 'absolute', top: 16, right: 16 }}
              >
                {tier.badge}
              </Badge>
            )}

            <Stack gap="md">
              <div>
                <Text size="sm" fw={600} c="blue" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                  {tier.name}
                </Text>
                <Group gap={4} align="baseline" mt={8}>
                  {tier.price ? (
                    <>
                      <Title order={2} fw={900} fz={30}>
                        {tier.price}
                      </Title>
                      {tier.period && (
                        <Text size="sm" c="dimmed">/{tier.period}</Text>
                      )}
                    </>
                  ) : (
                    <Title order={2} fw={900} fz={24}>
                      Custom
                    </Title>
                  )}
                </Group>
              </div>

              <Text size="sm" c="dimmed" lh={1.6}>
                {tier.description}
              </Text>

              <Divider />

              <List spacing="sm" center>
                {tier.features.map((feature) => (
                  <List.Item
                    key={feature}
                    icon={
                      <ThemeIcon color={tier.highlight ? 'blue' : 'gray'} size={20} radius="xl" variant="light">
                        <Check size={12} />
                      </ThemeIcon>
                    }
                  >
                    <Text size="sm" c="dimmed">{feature}</Text>
                  </List.Item>
                ))}
              </List>

              {tier.name === 'Custom' ? (
                <Button
                  fullWidth
                  variant="filled"
                  color="blue"
                  rightSection={<ArrowRight size={16} />}
                  mt="auto"
                  onClick={() => setShowCalendar(true)}
                >
                  {tier.cta}
                </Button>
              ) : tier.plan ? (
                <Button
                  fullWidth
                  variant={tier.highlight ? 'filled' : 'outline'}
                  color={tier.highlight ? 'blue' : 'gray'}
                  rightSection={<ArrowRight size={16} />}
                  mt="auto"
                  onClick={() => openCheckout(tier.plan)}
                >
                  {tier.cta}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant={tier.highlight ? 'filled' : 'outline'}
                  color={tier.highlight ? 'blue' : 'gray'}
                  rightSection={<ArrowRight size={16} />}
                  component="a"
                  href={tier.href}
                  mt="auto"
                >
                  {tier.cta}
                </Button>
              )}
            </Stack>
          </Card>
        ))}
      </SimpleGrid>

      <Group justify="center" mt={48}>
        <Text size="sm" c="dimmed">
          Already have a subscription?{' '}
          <Anchor href="/api/portal" size="sm">
            Manage your subscription
          </Anchor>
        </Text>
      </Group>

      <Modal
        opened={showCalendar}
        onClose={() => setShowCalendar(false)}
        title="Schedule a call — Custom"
        size="xl"
        styles={{ body: { height: '80vh', padding: 0 } }}
      >
        <iframe
          src="/api/contact"
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Schedule Enterprise Call"
        />
      </Modal>
    </Container>
  );
}
